import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { WORKER_ATTR_NAME } from '../../utils/constants';
import { getLiquidTemplate } from '../../utils/get-liquid-template';
import { toDataAttrs } from '../../utils/to-data-attrs';
import WorkerAttributeNodeView from './worker-attribute-node-view';

import type { WorkerAttr } from '@/types/worker-attr';

type WorkerAttrNodeAttributes = {
  [K in keyof Required<WorkerAttr>]: {
    default: WorkerAttr[K];
  };
};

export const WorkerAttributeExtension = Node.create({
  name: WORKER_ATTR_NAME,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: '',
        ...toDataAttrs('id'),
      },
      label: {
        default: '',
        ...toDataAttrs('label'),
      },
      fallback: {
        default: '',
        ...toDataAttrs('fallback'),
      },
      tag: {
        default: 'worker',
        ...toDataAttrs('tag'),
      },
      dataType: {
        default: 'string',
        ...toDataAttrs('dataType'),
      },
      attrType: {
        default: undefined,
        ...toDataAttrs('attrType'),
      },
    } as WorkerAttrNodeAttributes;
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderText({ node }) {
    // Check if the node already has text content (workaround for TipTap bug)
    // See: https://github.com/ueberdosis/tiptap/pull/3410
    const hasTextAlready = node.content?.content?.length;
    if (hasTextAlready) {
      return '';
    }

    // Ensure we have valid attributes with fallbacks
    const attrs = node.attrs as Partial<WorkerAttr>;
    const tag = attrs.tag ?? 'worker';
    const id = attrs.id ?? '';
    const { fallback } = attrs;
    const dataType = attrs.dataType ?? 'string';

    return getLiquidTemplate(tag, id, fallback, dataType);
  },

  renderHTML({ HTMLAttributes }) {
    const tag = (HTMLAttributes['data-tag'] ?? 'worker') as WorkerAttr['tag'];
    const id = (HTMLAttributes['data-id'] ?? '') as string;
    const fallback = HTMLAttributes['data-fallback'] as string | undefined;
    const dataType = (HTMLAttributes['data-dataType'] ??
      'string') as WorkerAttr['dataType'];
    const label = HTMLAttributes['data-label'] as string | undefined;

    const template = getLiquidTemplate(tag, id, fallback, dataType);

    return [
      'span',
      mergeAttributes(
        { 'data-type': this.name },
        {
          'data-id': id,
          'data-label': label,
          'data-fallback': fallback,
          'data-tag': tag,
          'data-dataType': dataType,
          class:
            'mx-[3px] rounded-md bg-blue-50 px-[4px] py-[1px] text-sm text-blue-700',
        },
      ),
      template,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(WorkerAttributeNodeView);
  },
});
